import NetInfo from "@react-native-community/netinfo"
import { useFocusEffect, useIsFocused, useNavigation } from "@react-navigation/native"
import {
  ButtonType,
  H6,
  useAlert,
  useStyles,
  useTheme,
  VectorIconProps,
  VideoPlayer,
} from "capsule"
import dayjs from "dayjs"
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { BackHandler, Platform, View } from "react-native"
import { IconButton } from "react-native-paper"
import { OnVideoErrorData } from "react-native-video"

import { ID, SpecialtyName } from "../../common/CommonUserData"
import CountdownOverlay from "../../components/CountdownOverlay"
import EmptyVideo from "../../components/EmptyVideo"
import { ICON_SIZE_MEDIUM } from "../../features/config/Constants"
import { ExerciseNS } from "../../features/i18n/constants"
import { useProgram } from "../../features/Providers/ProgramProvider"
import { sessionContext } from "../../features/Providers/SessionProvider"
import { useVideo } from "../../features/Providers/VideoProvider"

interface IProps {
  id: ID
  uri?: string
  reps?: number
  series?: number
  readOnly?: boolean
  specialty?: SpecialtyName
  isFullScreenPrincipal?: boolean
  setIsFullScreenPrincipal?: Dispatch<SetStateAction<boolean>>
  disabled?: boolean
}

const ExerciseVideoView: FC<IProps> = ({
  uri,
  id,
  reps,
  series,
  readOnly,
  specialty,
  isFullScreenPrincipal,
  setIsFullScreenPrincipal,
  disabled,
}) => {
  // global contexts
  const {
    colors: { primary: localPrimary, white: localWhite },
  } = useTheme()

  const navigation = useNavigation<any>()
  const isFocused = useIsFocused()
  const { t } = useTranslation(ExerciseNS)
  const { showDialog, setIsValid } = useAlert()

  // context to control tabs and phase screen
  const { prefTabVideo, setPrefTabVideo } = useProgram()

  // context to control the exercise screen and its video
  const {
    videoRef,
    videoState,
    dispatch,
    actions: { reset, pause, resume, start },
    timeLeft,
    duration,
    videoError,
    setVideoError,
  } = useVideo()
  const { startExercise, cancelSession } = useContext(sessionContext)
  const isOnco = useMemo(() => specialty === "onco", [specialty])
  const isOrthoOrImplant = useMemo(
    () => specialty === "ortho" || specialty === "implant",
    [specialty],
  )
  const [showReplayButton, setShowReplayButton] = useState(false)
  const [showCountDown, setShowCountDown] = useState(true)
  const [controlledPause, setControlledPause] = useState(duration ? false : true)
  const [videoKey, setVideoKey] = useState(0)

  const s = useStyles(
    ({ dimensions: { spacing }, colors: { primary, white } }) => ({
      buttonNegative: { color: primary, right: spacing / 4 },
      buttonPositive: { right: spacing / 4 },
      control: {
        paddingTop: spacing,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      },
      replay: {
        alignSelf: "center",
        height: "100%",
        justifyContent: "center",
        position: "absolute",
        color: white.mediumEmphasis,
      },
      time: {
        borderRadius: 24,
        position: "absolute",
        right: spacing / 2,
        bottom: spacing / 2,
        backgroundColor: white.highEmphasis,
        paddingVertical: spacing / 4,
        paddingHorizontal: spacing / 2,
      },
    }),
    [],
  )

  const icon = useMemo<VectorIconProps>(
    () => ({
      name: "play-circle-fill",
      category: "MaterialIcons",
      size: ICON_SIZE_MEDIUM,
      color: localWhite.highEmphasis,
    }),
    [localWhite],
  )

  const onError = useCallback(
    (error: OnVideoErrorData) => {
      setVideoError(error)
    },
    [setVideoError],
  )

  const reload = useCallback(() => {
    setVideoError(undefined)
    if (videoRef?.current) {
      videoRef.current.seek(0)
    }
  }, [videoRef, setVideoError])

  useEffect(() => {
    NetInfo.addEventListener(state => {
      if (state.isConnected) {
        setVideoError(undefined)
      }
    })
  }, [setVideoError])

  useEffect(() => {
    if (prefTabVideo) {
      // avoid bad behaviour with useCountDown
      setControlledPause(true)
      setTimeout(() => pause(), 0)
      dispatch({ type: "paused" })
    }
  }, [dispatch, pause, prefTabVideo])

  useEffect(() => {
    if (isOrthoOrImplant && timeLeft === 0 && !videoState.firstPress) {
      if (duration) {
        dispatch({ type: "paused" })
      }
      setShowReplayButton(true)
      dispatch({ type: "ready" })
    }
  }, [timeLeft, dispatch, reset, isOrthoOrImplant, videoState.firstPress, duration])

  const isPaused = videoState.paused || disabled === true
  const isControlledPaused = controlledPause === true || disabled === true

  const onVideoPress = useCallback(() => {
    // Bloquer l'interaction uniquement si disabled
    if (disabled) {
      return
    }

    if (videoState.firstPress && prefTabVideo) {
      setPrefTabVideo(false)
      start(duration)
      dispatch({ type: "start" })
      return
    }
    if (videoState.paused) {
      if (duration) {
        setControlledPause(false)
      }
      resume()
      setPrefTabVideo(false)
    } else {
      if (duration) {
        setControlledPause(true)
      }
      pause()
    }
    dispatch({ type: "play" })
  }, [
    videoState.firstPress,
    videoState.paused,
    prefTabVideo,
    dispatch,
    setPrefTabVideo,
    start,
    duration,
    resume,
    pause,
    disabled,
  ])

  const restartExercise = useCallback(() => {
    if (duration) {
      setShowCountDown(true)
      setControlledPause(true)
    }
    setShowReplayButton(false)
    dispatch({ type: "restart" })
    reset()
    if (Platform.OS === "android") {
      setVideoKey(prev => prev + 1)
    }
    setTimeout(() => videoRef?.current?.seek(0), 0)
  }, [dispatch, reset, videoRef, duration])

  const onCountdownFinish = useCallback(() => {
    if (disabled) {
      return
    }

    setShowReplayButton(false)
    setShowCountDown(false)
    if (Platform.OS === "android") {
      setTimeout(() => {
        setVideoKey(prev => prev + 1)
        setTimeout(() => {
          if (videoState.restart === 0 && !readOnly) {
            startExercise()
          }
          if (!prefTabVideo) {
            start(duration)
            dispatch({ type: "start" })
          } else {
            dispatch({ type: "ready" })
          }
          if (duration) {
            setControlledPause(false)
          }
        }, 50)
      }, 100)
    } else {
      if (videoState.restart === 0 && !readOnly) {
        startExercise()
      }
      if (!prefTabVideo) {
        start(duration)
        dispatch({ type: "start" })
      } else {
        dispatch({ type: "ready" })
      }
      if (duration) {
        setControlledPause(false)
      }
    }
  }, [
    videoState.restart,
    readOnly,
    prefTabVideo,
    startExercise,
    start,
    duration,
    dispatch,
    disabled,
  ])

  const renderDuration = useCallback(
    () => (
      <>
        {specialty ? (
          <View style={s.time}>
            <H6 color={localPrimary}>
              {isOrthoOrImplant
                ? dayjs(timeLeft).format("mm:ss")
                : `${series} x ${reps} répétitions`}
            </H6>
          </View>
        ) : null}
      </>
    ),
    [localPrimary, reps, series, s.time, specialty, isOrthoOrImplant, timeLeft],
  )

  const onEnd = useCallback(() => {
    if (readOnly) {
      navigation.goBack()
    } else if (isOnco) {
      setShowReplayButton(true)
      return
    } else if (duration && timeLeft > 0) {
      setShowReplayButton(false)
      setPrefTabVideo(false)
      dispatch({ type: "restart" })
      setTimeout(() => videoRef?.current?.seek(0), 0)
      setTimeout(() => dispatch({ type: "start" }), 0)
    }
  }, [isOnco, navigation, readOnly, timeLeft, dispatch, duration, setPrefTabVideo, videoRef])

  /** Android BackHandler */
  const goBack = useCallback((): boolean => {
    if (readOnly) {
      navigation.goBack()
      return true
    }

    setIsValid?.(true)

    Promise.resolve(
      showDialog({
        type: "button",
        message: t("alert.message"),
        title: t("alert.title"),
        positive: {
          type: ButtonType.POSITIVE,
          textStyle: s.buttonPositive,
          buttonStyle: { borderWidth: 0, flex: undefined },
          label: t("abandon"),
        },
        negative: {
          type: ButtonType.NEGATIVE,
          textStyle: s.buttonNegative,
          buttonStyle: { flex: undefined, borderWidth: 0 },
          label: t("common:button.cancel"),
        },
      }),
    ).then(result => {
      if (result && result.button === ButtonType.POSITIVE) {
        Promise.resolve(cancelSession()).then(() => {
          navigation.navigate("Home")
        })
      }
    })

    return true
  }, [
    readOnly,
    navigation,
    setIsValid,
    showDialog,
    t,
    s.buttonPositive,
    s.buttonNegative,
    cancelSession,
  ])

  useFocusEffect(
    useCallback(() => {
      const sub = BackHandler.addEventListener("hardwareBackPress", goBack)
      return () => sub.remove()
    }, [goBack]),
  )

  return (
    <>
      <View collapsable={false}>
        {(videoState.restart === 0 || (showCountDown && controlledPause)) && !disabled && (
          <CountdownOverlay
            isPlaying={isFocused}
            key={`countdown-${id}`}
            onFinish={onCountdownFinish}
            restart={videoState.restart}
          />
        )}
        {!uri || videoError ? (
          <EmptyVideo onReload={reload} />
        ) : (
          <VideoPlayer
            hasExtraHeader={false}
            enableFullScreen={true}
            isFullScreenPrincipal={isFullScreenPrincipal}
            setIsFullScreenPrincipal={setIsFullScreenPrincipal}
            key={`video-${id}-${videoKey}`}
            forwardRef={videoRef}
            showProgress={true}
            {...{ onVideoPress, renderDuration, uri, icon }}
            alternativeProgress={duration ? ((duration - timeLeft) / duration) * 100 : undefined}
            controlledPause={isControlledPaused}
            control={{
              onEnd,
              onError,
              paused: isPaused,
            }}
          />
        )}
        {showReplayButton && !videoError && !disabled ? (
          <View style={s.replay}>
            <IconButton
              icon="replay"
              size={96}
              rippleColor={localPrimary}
              onPress={restartExercise}
            />
          </View>
        ) : null}
      </View>
    </>
  )
}

export default ExerciseVideoView
